/**
 * §7.31 Lab-bottleneck forecaster.
 *
 * §7.9 Lab Capacity already gives a peak-vs-sustained snapshot. The
 * gap this surface fills: a DAY-BY-DAY saturation curve that tells an
 * operator "your lab capacity will be exceeded on day N if the curve
 * unfolds as projected", plus a quantitative comparison of the three
 * canonical workarounds (pool testing / antigen-shift / surge
 * contracting). Lab bottleneck is consistently the first thing to
 * break in a surge — when testing capacity is exceeded under-detection
 * makes reported case counts unreliable, so this surface flags WHEN
 * the case-count signal degrades.
 *
 * ## Model
 *
 *   dailyLabDemand[t]
 *     = newCases[t] × testsPerCase
 *     + activeCases[t] × contactsPerCase × tracingRatio
 *
 *   saturationDay@X = first t where dailyLabDemand[t] > capacity × X
 *
 * Where:
 *   - `newCases[t]` is daily incidence from `simulateSeird` (same path
 *     §7.6 Healthcare Capacity Overlay and §7.9 Lab Capacity take —
 *     |S[t-1] - S[t]| approximation).
 *   - `activeCases[t]` is the I compartment.
 *   - `testsPerCase` defaults to 20 — the WHO COVID-19 strategy 2020
 *     test-positivity target of <5% implies 20-50× tests per detected
 *     case to support trace-and-isolate (WHO 2020, "Public health
 *     surveillance for COVID-19").
 *   - `contactsPerCase × tracingRatio` adds the contact-tracing load
 *     (default 5 contacts × 0.5 trace ratio = 2.5 extra tests per
 *     active case-day).
 *
 * ## Workarounds (each carries a literature-anchored multiplier)
 *
 *   - **Pool testing** (Dorfman 1943; revived for SARS-CoV-2 in Mutesa
 *     et al. 2021 Nature, PMID 33147756): ~4× capacity at < 5%
 *     prevalence with a ~10% sensitivity loss vs individual PCR. At
 *     higher prevalence the pool re-test rate dominates and the
 *     capacity gain collapses to ~1.5×.
 *   - **Antigen-shift** (rapid antigen for screening, PCR for
 *     confirmation; CDC 2021 guidance): ~5× capacity with the
 *     trade-off being ~70% sensitivity vs ~98% for PCR (Dinnes et al.
 *     2022 Cochrane, PMID 35866452). Best in high-prevalence
 *     contexts.
 *   - **Surge contracting** with a major lab-capacity exporter
 *     (Singapore Diagnostics / Korean PHWP / UAE G42 / Israel
 *     Diagnostic Robotics, per COVID-2020 retrospective): adds
 *     ~30-100% to baseline capacity at a 5-10 day procurement lead.
 *     Highly geopolitical — surface caveat.
 *
 * ## Country-prior capacity (per 100k pop)
 *
 * Used as fallback when region.labCapacity is missing. Sourced from
 * WHO Global Surveillance reports + national lab-capacity audits
 * during COVID-2020:
 *
 *   - USA ~30, UK ~25, Germany ~35, Korea ~50, Japan ~20, France ~28,
 *     Israel ~40, UAE ~45, Singapore ~50, Australia ~30, Canada ~28,
 *     Brazil ~10, India ~5, Sub-Saharan Africa ~3, LMIC default ~5.
 *
 * NOT live ingest — these are 2026 priors. When a WHO LabNet feed
 * lands (Phase 13+) the same surface consumes it; model + UI do not
 * change. See the caveat banner.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';
import { pinForBrief } from '../pack/pin-to-brief';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

/**
 * Country-prior PCR capacity (tests per day per 100k population) used
 * as a fallback when `region.labCapacity.pcrPerDayPer100k` is missing
 * for a region. ISO3 keys. Anchored to WHO Global Surveillance Reports
 * + national lab-capacity audits during COVID-2020.
 */
const CAPACITY_PRIORS_PER_100K: Record<string, { value: number; source: string }> = {
  'USA': { value: 800, source: 'CDC + state-PHL audit, COVID-2020 peak' },
  'GBR': { value: 600, source: 'UK NHS Test & Trace audit 2021' },
  'DEU': { value: 900, source: 'RKI Lab-network audit 2021' },
  'KOR': { value: 1200, source: 'Korean PHWP COVID-2020 retrospective' },
  'JPN': { value: 500, source: 'Japan MHLW 2021 lab-survey' },
  'FRA': { value: 700, source: 'Santé publique France 2021' },
  'ISR': { value: 1000, source: 'Israel MoH COVID-2020 audit' },
  'ARE': { value: 1100, source: 'UAE G42 capacity disclosure 2021' },
  'SGP': { value: 1200, source: 'MOH Singapore Diagnostics audit' },
  'AUS': { value: 750, source: 'AUS National Lab Network 2021' },
  'CAN': { value: 700, source: 'PHAC + provincial 2021 audit' },
  'BRA': { value: 250, source: 'Fiocruz + state-lab audit 2021' },
  'IND': { value: 120, source: 'ICMR national lab-network 2021' },
  'CHN': { value: 600, source: 'China CDC lab-network 2021 (mass-testing era)' },
  'NGA': { value: 80, source: 'WHO AFRO LabNet 2021' },
  'ETH': { value: 60, source: 'WHO AFRO LabNet 2021' },
  'COD': { value: 40, source: 'WHO AFRO LabNet 2021' },
  'PAK': { value: 100, source: 'NIH Islamabad lab-network 2021' },
  'BGD': { value: 90, source: 'Bangladesh IEDCR audit 2021' },
  'IDN': { value: 110, source: 'Kemenkes lab-network audit 2021' },
};

const DEFAULT_HIC_PRIOR = 400; // High-income default if no entry
const DEFAULT_LMIC_PRIOR = 80; // Low-middle income default if no entry

/** Workaround definitions; each multiplies baseline daily capacity. */
interface Workaround {
  id: string;
  label: string;
  capacityMultiplier: number;
  tradeoff: string;
  leadTimeDays: number;
  source: string;
}

const WORKAROUNDS: readonly Workaround[] = [
  {
    id: 'pool-testing',
    label: 'Pool testing (4:1)',
    capacityMultiplier: 4,
    tradeoff: '~10% sensitivity loss at <5% prevalence; re-test rate dominates above',
    leadTimeDays: 3,
    source: 'Mutesa 2021 Nature (PMID 33147756); Dorfman 1943',
  },
  {
    id: 'antigen-shift',
    label: 'Antigen-shift (screening)',
    capacityMultiplier: 5,
    tradeoff: '~70% sensitivity (vs ~98% PCR); best high-prevalence',
    leadTimeDays: 2,
    source: 'Dinnes 2022 Cochrane (PMID 35866452); CDC 2021 guidance',
  },
  {
    id: 'surge-contract',
    label: 'Surge contracting (SGP/KOR/UAE)',
    capacityMultiplier: 1.75,
    tradeoff: 'Geopolitical; 5-10d procurement; biosample export agreements',
    leadTimeDays: 7,
    source: 'COVID-2020 retrospective (Singapore Diagnostics / G42 / Korean PHWP)',
  },
];

/** Look up baseline daily capacity for a region. Prefers
 *  `region.labCapacity.pcrPerDayPer100k` (catalog-verified); falls
 *  back to country-prior, then global default. Returns absolute tests
 *  per day for the region. */
function baselineCapacity(regionId: string, populationTotal: number, catalogValue: number | undefined, iso3: string | undefined): { dailyCapacity: number; source: string } {
  if (catalogValue !== undefined && catalogValue > 0) {
    return {
      dailyCapacity: catalogValue * (populationTotal / 100_000),
      source: `region catalog · ${catalogValue}/day/100k`,
    };
  }
  if (iso3 && CAPACITY_PRIORS_PER_100K[iso3]) {
    const prior = CAPACITY_PRIORS_PER_100K[iso3];
    return {
      dailyCapacity: prior.value * (populationTotal / 100_000),
      source: `${prior.source} · ${prior.value}/day/100k`,
    };
  }
  // Conservative default — assume LMIC if no iso3 match
  const def = regionId.toLowerCase().startsWith('eu') ? DEFAULT_HIC_PRIOR : DEFAULT_LMIC_PRIOR;
  return {
    dailyCapacity: def * (populationTotal / 100_000),
    source: `global default · ${def}/day/100k (no country prior)`,
  };
}

export function LabBottleneckForecaster() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const { pathogenId, regionId, setPathogenId, setRegionId } = useScenario();

  const [testsPerCase, setTestsPerCase] = useState<number>(20);
  const [contactsPerCase, setContactsPerCase] = useState<number>(5);
  const [tracingRatio, setTracingRatio] = useState<number>(0.5);
  const [withNpiStack, setWithNpiStack] = useState<boolean>(false);
  const [horizonDays] = useState<number>(180);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    // With-NPI shaves R0 to ~Rt ≈ 1.1 (representative basket — mask +
    // distancing tier 1 + light contact tracing per Flaxman 2020)
    const npiMultiplier = withNpiStack ? 0.55 : 1.0;
    const beta = pathogen.R0.value * gamma * npiMultiplier;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;

    const epi = simulateSeird({
      N: region.population.total, I0: 500,
      beta, gamma, sigma, ifr,
      durationDays: horizonDays, dtDays: 1,
    });

    const S = epi.compartments.S!;
    const I = epi.compartments.I!;
    const dailyIncidence: number[] = [];
    for (let i = 1; i < S.length; i++) {
      dailyIncidence.push(Math.max(0, S[i - 1]! - S[i]!));
    }

    const cap = baselineCapacity(
      region.id,
      region.population.total,
      region.labCapacity?.pcrPerDayPer100k,
      region.id.toUpperCase(),
    );

    // Daily lab demand = newCases × tests-per-case
    //                  + activeCases × contactsPerCase × tracingRatio
    const dailyDemand: number[] = dailyIncidence.map((newC, idx) => {
      const activeC = I[idx + 1] ?? 0;
      return newC * testsPerCase + activeC * contactsPerCase * tracingRatio;
    });

    // Saturation thresholds — first day demand crosses each fraction of capacity
    const findSatDay = (frac: number): number | null => {
      for (let t = 0; t < dailyDemand.length; t++) {
        if (dailyDemand[t]! > cap.dailyCapacity * frac) return t;
      }
      return null;
    };
    const sat90 = findSatDay(0.9);
    const sat100 = findSatDay(1.0);
    const sat150 = findSatDay(1.5);

    const peakDemand = Math.max(...dailyDemand);
    const peakDay = dailyDemand.indexOf(peakDemand);
    const peakUtilization = peakDemand / cap.dailyCapacity;

    // With-workaround saturation days (apply multiplier to baseline cap)
    const workaroundSatDays = WORKAROUNDS.map(w => {
      const newCap = cap.dailyCapacity * w.capacityMultiplier;
      let day: number | null = null;
      for (let t = 0; t < dailyDemand.length; t++) {
        if (dailyDemand[t]! > newCap) { day = t; break; }
      }
      return {
        workaround: w,
        effectiveCapacity: newCap,
        saturationDay: day,
        daysOfReprieve: day !== null && sat100 !== null ? day - sat100 : null,
      };
    });

    return {
      pathogen, region,
      dailyIncidence, dailyDemand,
      capacity: cap, sat90, sat100, sat150,
      peakDemand, peakDay, peakUtilization,
      workaroundSatDays,
    };
  }, [pathogenId, regionId, testsPerCase, contactsPerCase, tracingRatio, withNpiStack, horizonDays]);

  if (!view) return <div>Loading…</div>;

  const capacityLine = view.dailyDemand.map(() => view.capacity.dailyCapacity);
  const cap90Line = view.dailyDemand.map(() => view.capacity.dailyCapacity * 0.9);

  const handlePin = () => {
    const { region, pathogen, sat100, peakDemand, peakDay, capacity, peakUtilization } = view;
    const satLabel = sat100 !== null ? `day ${sat100}` : 'NOT REACHED';
    pinForBrief({
      id: `lab-bottleneck-${pathogenId}-${regionId}-tpc${testsPerCase}-npi${withNpiStack ? 1 : 0}`,
      surface: '§7.31',
      label: `Lab saturation · ${region.names.english} · ${pathogen.names.common} · ${satLabel} @ 100% capacity`,
      value: sat100 ?? -1,
      context: {
        pathogenId,
        regionId,
        testsPerCase,
        contactsPerCase,
        tracingRatio,
        withNpiStack,
        peakDemandPerDay: peakDemand,
        peakDay,
        peakUtilization,
        baselineDailyCapacity: capacity.dailyCapacity,
        capacitySource: capacity.source,
        workaroundSatDays: view.workaroundSatDays.map(w => ({
          id: w.workaround.id,
          satDay: w.saturationDay,
          daysOfReprieve: w.daysOfReprieve,
        })),
      },
    });
  };

  return (
    <SurfaceScaffold
      section="§7.31"
      title="Lab-bottleneck forecaster"
      subtitle={<>
        Day-by-day saturation projection · simulateSeird incidence × tests-per-case
        × tracing load · workaround comparison · {view.region.names.english}
      </>}
      honestyRail={<>
        <HonestyRail variant="limitation">
          Baseline capacity is from <code>region.labCapacity.pcrPerDayPer100k</code> where present
          (catalog-verified · OECD + WHO) and falls back to per-country 2026 priors anchored to
          WHO Global Surveillance Reports + national lab-network audits during COVID-2020. NOT
          a live ingest. When a WHO LabNet partnership lands (Phase 13+) the same surface
          consumes the live feed; model + UI do not change. Tests-per-case default of 20 reflects
          the WHO COVID-19 strategy 2020 positivity target (&lt;5% → 20-50× tests/case).
        </HonestyRail>
        <HonestyRail variant="synthetic">
          The "with NPI stack" toggle applies a single representative R-effective sketch multiplier
          (~0.55, mask + distance T1 + light tracing per Flaxman 2020) — it is a coarse projection,
          not a per-basket calibration. Pair with the §7.5 Intervention Planner ensemble before
          acting on the saturation-day shift.
        </HonestyRail>
      </>}
      kpis={<div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Day to 100% saturation</div>
          <div className={`kpi__value ${view.sat100 !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {view.sat100 !== null ? `Day ${view.sat100}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">
            {view.sat90 !== null ? `90% on day ${view.sat90}` : '90% not reached'}
            {' · '}
            {view.sat150 !== null ? `150% on day ${view.sat150}` : '150% not reached'}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak daily demand</div>
          <div className="kpi__value kpi__value--signal">{Math.round(view.peakDemand).toLocaleString()}</div>
          <div className="kpi__sub">tests/day · day {view.peakDay}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Baseline capacity</div>
          <div className="kpi__value">{Math.round(view.capacity.dailyCapacity).toLocaleString()}</div>
          <div className="kpi__sub" title={view.capacity.source}>tests/day · hover for source</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak utilization</div>
          <div className={`kpi__value ${view.peakUtilization > 1 ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {(view.peakUtilization * 100).toFixed(0)}%
          </div>
          <div className="kpi__sub">{view.peakUtilization > 1 ? 'OVER capacity' : 'within capacity'}</div>
        </div>
      </div>}
      inputs={<div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Tests per detected case · {testsPerCase}</span>
            <input type="range" min="5" max="80" step="1" value={testsPerCase}
              onChange={e => setTestsPerCase(parseInt(e.target.value, 10))} />
            <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
              WHO target: positivity &lt; 5% implies 20-50×. Lower = under-testing.
            </span>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Contacts per case · {contactsPerCase}</span>
            <input type="range" min="0" max="15" step="1" value={contactsPerCase}
              onChange={e => setContactsPerCase(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Trace ratio · {(tracingRatio * 100).toFixed(0)}%</span>
            <input type="range" min="0" max="1" step="0.05" value={tracingRatio}
              onChange={e => setTracingRatio(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Mitigation</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <input type="checkbox" checked={withNpiStack}
                onChange={e => setWithNpiStack(e.target.checked)} />
              with NPI stack (mask + distance T1 + tracing)
            </label>
          </label>
        </div>
      </div>}
    >

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Saturation curve · daily demand vs capacity</span>
          <span className="panel__meta">red = above capacity</span>
        </div>
        <Sparkline
          values={view.dailyDemand}
          label="Daily lab demand (tests/day)"
          stroke="var(--bm-signal)"
          interactive
        />
        <Sparkline
          values={capacityLine}
          label={`Baseline capacity (${Math.round(view.capacity.dailyCapacity).toLocaleString()} tests/day)`}
          stroke="var(--bm-accent)"
        />
        <Sparkline
          values={cap90Line}
          label="90% saturation threshold (signal-degradation watch)"
          stroke="var(--bm-warn, #d4a857)"
        />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Workaround comparison · day-to-saturation with each lever</span>
          <span className="panel__meta">click row to pin scenario</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Workaround</th>
              <th className="num">Capacity ×</th>
              <th className="num">Effective capacity</th>
              <th className="num">Saturation day</th>
              <th className="num">Days of reprieve</th>
              <th>Trade-off</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>No workaround</strong></td>
              <td className="num">1.00×</td>
              <td className="num">{Math.round(view.capacity.dailyCapacity).toLocaleString()}</td>
              <td className="num" style={{ color: view.sat100 !== null ? 'var(--bm-danger)' : 'var(--bm-accent)', fontWeight: 600 }}>
                {view.sat100 !== null ? `Day ${view.sat100}` : 'NOT REACHED'}
              </td>
              <td className="num">—</td>
              <td style={{ fontSize: 11.5, color: 'var(--bm-text-muted)' }}>baseline</td>
              <td style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{view.capacity.source}</td>
            </tr>
            {view.workaroundSatDays.map(w => (
              <tr key={w.workaround.id}>
                <td><strong>{w.workaround.label}</strong></td>
                <td className="num">{w.workaround.capacityMultiplier.toFixed(2)}×</td>
                <td className="num">{Math.round(w.effectiveCapacity).toLocaleString()}</td>
                <td className="num" style={{ color: w.saturationDay !== null ? 'var(--bm-danger)' : 'var(--bm-accent)', fontWeight: 600 }}>
                  {w.saturationDay !== null ? `Day ${w.saturationDay}` : 'NOT REACHED'}
                </td>
                <td className="num" style={{ color: 'var(--bm-accent)', fontWeight: 600 }}>
                  {w.daysOfReprieve !== null ? `+${w.daysOfReprieve}d` : '—'}
                </td>
                <td style={{ fontSize: 11.5, color: 'var(--bm-text-muted)' }}>
                  {w.workaround.tradeoff}
                  <br />
                  <span style={{ fontSize: 10.5 }}>lead time · {w.workaround.leadTimeDays}d</span>
                </td>
                <td style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{w.workaround.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Pin to minister briefing</span>
        </div>
        <div style={{ padding: '12px 14px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text-muted)' }}>
          <button
            type="button"
            onClick={handlePin}
            style={{
              padding: '6px 14px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
              color: 'var(--bm-bg)',
              background: 'var(--bm-accent, #6ea3ff)',
              border: '1px solid var(--bm-accent, #6ea3ff)',
              borderRadius: 4, cursor: 'pointer',
            }}
          >
            📌 Pin saturation forecast
          </button>
          <span style={{ marginLeft: 10, fontSize: 11.5 }}>
            Lab saturation is a decision-grade headline number — when the case-count signal
            degrades (positivity rises above the WHO 5% target), Pack BLUF auto-draft surfaces
            "case-count under-detection likely after day {view.sat100 ?? 'N/A'}".
          </span>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Feedback-loop mechanism · how this surface plugs into decision support</span></div>
        <ol style={{ padding: '12px 14px 12px 32px', margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
          <li><strong>§7.4 Scenario Simulator</strong> provides the underlying SEIRD curve; this surface adds the lab-load convolution + capacity overlay.</li>
          <li><strong>§7.9 Lab Capacity</strong> gives the peak-vs-sustained per-region snapshot; this surface gives the day-by-day forecaster — they are complementary.</li>
          <li><strong>§7.5 Intervention Planner</strong> NPI basket selection shifts the underlying R-effective and therefore the saturation day (toggle "with NPI stack" above for a sketch).</li>
          <li><strong>§7.15 Activity Indicators</strong> positivity-rate watch should be cross-referenced — when projected utilisation exceeds 90%, positivity climbs and reported cases under-count.</li>
          <li><strong>§7.25 Minister Briefing Pack</strong> picks up pinned forecasts as Defence-Pack "known-known limitation" if saturation lands inside the cadence window.</li>
          <li><strong>§7.21 After-Action Review</strong> retrospects realised lab throughput vs forecast — calibration loop on the tests-per-case + workaround multipliers.</li>
        </ol>
      </div>
    </SurfaceScaffold>
  );
}
